import { Component } from '@theme/component';

/**
 * A custom element that manages the cart upsell section.
 * Handles carousel navigation and add-to-cart functionality.
 *
 * @extends {Component}
 */
class CartUpsellComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initCarousel();
    this.initAddToCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Initializes the carousel navigation.
   */
  initCarousel() {
    const track = this.querySelector('#cart-upsell-track');
    const prevBtn = this.querySelector('.cart-upsell__arrow--prev');
    const nextBtn = this.querySelector('.cart-upsell__arrow--next');
    const slides = this.querySelectorAll('.cart-upsell__slide');

    if (!track || !prevBtn || !nextBtn || slides.length === 0) return;

    const getSlideWidth = () => {
      const slide = slides[0];
      const style = getComputedStyle(track);
      const gap = parseInt(style.gap) || 16;
      return slide.offsetWidth + gap;
    };

    const scroll = (direction) => {
      const slideWidth = getSlideWidth();
      const scrollAmount = direction === 'next' ? slideWidth : -slideWidth;
      track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    prevBtn.addEventListener('click', () => scroll('prev'));
    nextBtn.addEventListener('click', () => scroll('next'));
  }

  /**
   * Initializes the add-to-cart functionality using event delegation.
   */
  initAddToCart() {
    this.addEventListener('click', this.#handleClick);
  }

  /**
   * Handles click events on the component.
   * @param {MouseEvent} e - The click event.
   */
  #handleClick = async (e) => {
    const btn = e.target.closest('.cart-upsell__card-atc');
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    e.preventDefault();

    btn.classList.add('loading');
    btn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cartResponse = await fetch('/cart.js');
      const fullCart = await cartResponse.json();

      document.dispatchEvent(new CustomEvent('cart:add', { bubbles: true }));
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: fullCart,
            sourceId: 'cart-upsell',
            data: {
              itemCount: fullCart.item_count,
              source: 'cart-upsell',
              variantId: parseInt(variantId),
            },
          },
        }),
      );
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
